'use client';

import {
  marketPoolQuoteContextAtTime,
  quoteOutcomeBuyExactIn,
  quoteOutcomeSellFeeExcludedExactIn,
  quoteOutcomeSellForQuoteOutput,
} from '@morfi/backend-sdk-v2/client';

import {
  absolutePriceImpactBps,
  effectiveExecutionPriceD9,
  type OutcomePoolQuoteContext,
  outcomeSpotPriceD9,
  type PoolMathUnavailableReason,
} from '@morfi/market-math';
import BN from 'bn.js';
import { D6_SCALE, D9_SCALE } from '@/config/solana';

export interface TradeEstimateParams {
  direction: 'buy' | 'sell';
  /** User-entered quote amount, converted to D6 only at this UI boundary. */
  amountUsdc: number;
  context: OutcomePoolQuoteContext;
  /** Exact wallet-limited sell capacity (outcome tokens, D6). */
  maximumOutcomeAmountInD6: bigint;
  slippageBps: number;
}

export interface BuyTradeEstimate {
  direction: 'buy';
  amountIn: number;
  totalCost: number;
  /** Quote-denominated fees only; output-token fees are reported separately. */
  totalFees: number;
  buyFee: number;
  prizePoolFee: number;
  swapFee: number;
  tokensOut: number;
  effectivePrice: number;
  spotPrice: number;
  priceImpactBps: number;
  graduationTriggered: false;
  marketPhase: 'bonding_curve' | 'amm';
  suggestedSlippageBps: number;
  amountInD6: BN;
  /** Exact quote-token wallet debit (D6). BC includes fees on top; CP remains exact input. */
  walletDebitD6: BN;
  /** Fee-excluded quote amount that entered the pricing curve (D6). */
  feeExcludedQuoteInputD6: BN;
  /** Exact SDK quote output used for executable slippage protection. */
  outcomeTokensOutD6: BN;
  buyFeeD6: BN;
  prizePoolFeeD6: BN;
  /** CP-only fee charged from the quote-token input, including creator fee when configured there. */
  quoteInputFeeD6: BN;
  /** CP-only creator fee charged from the outcome-token output. */
  outcomeOutputCreatorFeeD6: BN;
}

export interface SellTradeEstimate {
  direction: 'sell';
  tokensNeeded: number;
  grossProceeds: number;
  netUsdcReceived: number;
  /** Quote-denominated fees only; input-token fees are reported separately. */
  totalFees: number;
  baseFee: number;
  prizePoolFee: number;
  penaltyFee: number;
  swapFee: number;
  isPenalty: boolean;
  effectivePrice: number;
  spotPrice: number;
  priceImpactBps: number;
  marketPhase: 'bonding_curve' | 'amm';
  suggestedSlippageBps: number;
  tokensNeededD6: BN;
  /** Exact gross quote output/user intent from the SDK quote. */
  grossQuoteAmountD6: BN;
  /** Fee-excluded quote proceeds for gross P&L accounting. */
  feeExcludedQuoteOutputD6: BN;
  expectedNetUsdcOutD6: BN;
  sellBaseFeeD6: BN;
  prizePoolFeeD6: BN;
  penaltyFeeD6: BN;
  /** CP-only trade fee plus any creator fee charged from the outcome-token input. */
  outcomeInputFeeD6: BN;
  /** CP-only creator fee charged from the quote-token output. */
  quoteOutputCreatorFeeD6: BN;
}

export type TradeEstimate = BuyTradeEstimate | SellTradeEstimate;

export type TradeEstimateResult =
  | { available: true; value: TradeEstimate }
  | { available: false; reason: PoolMathUnavailableReason | 'no_amount' };

const D6: bigint = BigInt(D6_SCALE);
const D9: bigint = BigInt(D9_SCALE);
const MAX_SAFE_USDC: number = Math.floor(Number.MAX_SAFE_INTEGER / Number(D6));
const BC_MIN_SLIPPAGE_BPS = 100;
const BC_MAX_SLIPPAGE_BPS = 2_000;
const AMM_MIN_SLIPPAGE_BPS = 30;
const AMM_MAX_SLIPPAGE_BPS = 500;

/** Pure shared-quote adapter. All executable money math lives in market-math. */
export function computeTradeEstimateResult(
  params: TradeEstimateParams | null,
): TradeEstimateResult {
  if (params === null || params.amountUsdc <= 0) {
    return { available: false, reason: 'no_amount' };
  }
  const amountD6 = displayUsdcToD6(params.amountUsdc);
  if (amountD6 === null) return { available: false, reason: 'invalid_amount' };

  const context = marketPoolQuoteContextAtTime(params.context);
  const spot = outcomeSpotPriceD9(context);
  if (!spot.available) return spot;

  if (params.direction === 'buy') {
    const quote = quoteOutcomeBuyExactIn({
      context,
      quoteAmountInD6: amountD6,
    });
    if (!quote.available) return quote;
    const marketPhase = quote.value.venue === 'bonding_curve' ? 'bonding_curve' : 'amm';
    const outcomeOutD6 =
      quote.value.venue === 'bonding_curve'
        ? quote.value.outcomeAmountOutD6
        : quote.value.outputAmountD6;
    const totalDebitD6 =
      quote.value.venue === 'bonding_curve'
        ? quote.value.totalQuoteDebitD6
        : quote.value.inputAmountD6;
    const feeExcludedQuoteInputD6 =
      quote.value.venue === 'bonding_curve'
        ? quote.value.quoteToCurveD6
        : quote.value.curveInputAmountD6;
    const quoteInputFeeD6 =
      quote.value.venue === 'bonding_curve'
        ? quote.value.buyFeeD6 + quote.value.prizePoolFeeD6
        : quote.value.tradeFeeD6 +
          (quote.value.creatorFeeOnInput ? quote.value.creatorFeeD6 : BigInt(0));
    const outputCreatorFeeD6 =
      quote.value.venue === 'cp_swap' && !quote.value.creatorFeeOnInput
        ? quote.value.creatorFeeD6
        : BigInt(0);
    const effectivePrice = effectiveExecutionPriceD9(totalDebitD6, outcomeOutD6);
    if (!effectivePrice.available) return effectivePrice;
    const priceImpact = absolutePriceImpactBps(effectivePrice.value, spot.value);
    if (!priceImpact.available) return priceImpact;
    const effectivePriceD9 = effectivePrice.value;
    const priceImpactBps = Number(priceImpact.value);

    return {
      available: true,
      value: {
        direction: 'buy',
        amountIn: params.amountUsdc,
        totalCost: displayD6(totalDebitD6),
        totalFees: displayD6(quoteInputFeeD6),
        buyFee: quote.value.venue === 'bonding_curve' ? displayD6(quote.value.buyFeeD6) : 0,
        prizePoolFee:
          quote.value.venue === 'bonding_curve' ? displayD6(quote.value.prizePoolFeeD6) : 0,
        swapFee: quote.value.venue === 'cp_swap' ? displayD6(quoteInputFeeD6) : 0,
        tokensOut: displayD6(outcomeOutD6),
        effectivePrice: displayD9(effectivePriceD9),
        spotPrice: displayD9(spot.value),
        priceImpactBps,
        graduationTriggered: false,
        marketPhase,
        suggestedSlippageBps: suggestedSlippage(priceImpactBps, marketPhase),
        amountInD6: bigintToBn(amountD6),
        walletDebitD6: bigintToBn(totalDebitD6),
        feeExcludedQuoteInputD6: bigintToBn(feeExcludedQuoteInputD6),
        outcomeTokensOutD6: bigintToBn(outcomeOutD6),
        buyFeeD6: bigintToBn(
          quote.value.venue === 'bonding_curve' ? quote.value.buyFeeD6 : BigInt(0),
        ),
        prizePoolFeeD6: bigintToBn(
          quote.value.venue === 'bonding_curve' ? quote.value.prizePoolFeeD6 : BigInt(0),
        ),
        quoteInputFeeD6: bigintToBn(quote.value.venue === 'cp_swap' ? quoteInputFeeD6 : BigInt(0)),
        outcomeOutputCreatorFeeD6: bigintToBn(outputCreatorFeeD6),
      },
    };
  }

  const quote = quoteOutcomeSellForQuoteOutput({
    context,
    targetQuoteOutputD6: amountD6,
    target: 'gross',
    maximumOutcomeAmountInD6: params.maximumOutcomeAmountInD6,
  });
  if (!quote.available) return quote;

  const marketPhase = quote.value.venue === 'bonding_curve' ? 'bonding_curve' : 'amm';
  const tokensNeededD6 =
    quote.value.venue === 'bonding_curve'
      ? quote.value.outcomeAmountInD6
      : quote.value.inputAmountD6;
  const grossD6 =
    quote.value.venue === 'bonding_curve'
      ? quote.value.grossQuoteOutputD6
      : quote.value.curveOutputAmountD6;
  const netD6 =
    quote.value.venue === 'bonding_curve'
      ? quote.value.netQuoteOutputD6
      : quote.value.outputAmountD6;
  const feeExcludedQuote = quoteOutcomeSellFeeExcludedExactIn({
    context,
    outcomeAmountInD6: tokensNeededD6,
  });
  if (!feeExcludedQuote.available) return feeExcludedQuote;
  const quoteOutputCreatorFeeD6 =
    quote.value.venue === 'bonding_curve'
      ? quote.value.sellBaseFeeD6 + quote.value.prizePoolFeeD6 + quote.value.sellPenaltyFeeD6
      : quote.value.creatorFeeOnInput
        ? BigInt(0)
        : quote.value.creatorFeeD6;
  const outcomeInputFeeD6 =
    quote.value.venue === 'cp_swap'
      ? quote.value.tradeFeeD6 +
        (quote.value.creatorFeeOnInput ? quote.value.creatorFeeD6 : BigInt(0))
      : BigInt(0);
  const effectivePrice = effectiveExecutionPriceD9(netD6, tokensNeededD6);
  if (!effectivePrice.available) return effectivePrice;
  const priceImpact = absolutePriceImpactBps(effectivePrice.value, spot.value);
  if (!priceImpact.available) return priceImpact;
  const effectivePriceD9 = effectivePrice.value;
  const priceImpactBps = Number(priceImpact.value);

  return {
    available: true,
    value: {
      direction: 'sell',
      tokensNeeded: displayD6(tokensNeededD6),
      grossProceeds: displayD6(grossD6),
      netUsdcReceived: displayD6(netD6),
      totalFees: displayD6(quoteOutputCreatorFeeD6),
      baseFee: quote.value.venue === 'bonding_curve' ? displayD6(quote.value.sellBaseFeeD6) : 0,
      prizePoolFee:
        quote.value.venue === 'bonding_curve' ? displayD6(quote.value.prizePoolFeeD6) : 0,
      penaltyFee:
        quote.value.venue === 'bonding_curve' ? displayD6(quote.value.sellPenaltyFeeD6) : 0,
      swapFee: quote.value.venue === 'cp_swap' ? displayD6(quoteOutputCreatorFeeD6) : 0,
      isPenalty: quote.value.venue === 'bonding_curve' ? quote.value.penaltyApplied : false,
      effectivePrice: displayD9(effectivePriceD9),
      spotPrice: displayD9(spot.value),
      priceImpactBps,
      marketPhase,
      suggestedSlippageBps: suggestedSlippage(priceImpactBps, marketPhase),
      tokensNeededD6: bigintToBn(tokensNeededD6),
      grossQuoteAmountD6: bigintToBn(grossD6),
      feeExcludedQuoteOutputD6: bigintToBn(feeExcludedQuote.value),
      expectedNetUsdcOutD6: bigintToBn(netD6),
      sellBaseFeeD6: bigintToBn(
        quote.value.venue === 'bonding_curve' ? quote.value.sellBaseFeeD6 : BigInt(0),
      ),
      prizePoolFeeD6: bigintToBn(
        quote.value.venue === 'bonding_curve' ? quote.value.prizePoolFeeD6 : BigInt(0),
      ),
      penaltyFeeD6: bigintToBn(
        quote.value.venue === 'bonding_curve' ? quote.value.sellPenaltyFeeD6 : BigInt(0),
      ),
      outcomeInputFeeD6: bigintToBn(outcomeInputFeeD6),
      quoteOutputCreatorFeeD6: bigintToBn(
        quote.value.venue === 'cp_swap' ? quoteOutputCreatorFeeD6 : BigInt(0),
      ),
    },
  };
}

export function computeTradeEstimate(params: TradeEstimateParams | null): TradeEstimate | null {
  const result = computeTradeEstimateResult(params);
  return result.available ? result.value : null;
}

export function useTradeEstimate(params: TradeEstimateParams | null): TradeEstimate | null {
  return computeTradeEstimate(params);
}

function displayUsdcToD6(value: number): bigint | null {
  if (!Number.isFinite(value) || value <= 0 || value > MAX_SAFE_USDC) return null;
  const scaled = Math.round(value * Number(D6));
  if (!Number.isSafeInteger(scaled) || scaled <= 0) return null;
  return BigInt(scaled);
}

function suggestedSlippage(priceImpactBps: number, marketPhase: 'bonding_curve' | 'amm'): number {
  const minimum = marketPhase === 'amm' ? AMM_MIN_SLIPPAGE_BPS : BC_MIN_SLIPPAGE_BPS;
  const maximum = marketPhase === 'amm' ? AMM_MAX_SLIPPAGE_BPS : BC_MAX_SLIPPAGE_BPS;
  return Math.min(Math.max(priceImpactBps * 2, minimum), maximum);
}

function displayD6(value: bigint): number {
  return Number(value) / Number(D6);
}

function displayD9(value: bigint): number {
  return Number(value) / Number(D9);
}

function bigintToBn(value: bigint): BN {
  return new BN(value.toString());
}
